//=============================================================================
//                       <--- Developed By Icon Team ---> on 28 December 2020
//=============================================================================

// npm packages
import React, { useState, useCallback, useContext, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import moment from "moment";
import { CSVLink } from "react-csv";

// custom pages
import "assets/css/dashboard.css";
import Context from "context/context";
import Issued from "./Tabs/Issued";
import Redeemed from "./Tabs/Redeemed";
import EarnPending from "./Tabs/EarnPending";
import NonSales from "./Tabs/NonSales";
import Bonus from "./Tabs/Bonus";
import DashboardAPI from "apis/dashboard/dashboard";
import { useStyles } from "./styles";
import Filter from "./Filter/Filter";
import AvailablePointsCard from "./Components/AvailablePointsCard";
import {
  EarnedHeaders,
  RedeemedHeaders,
  PendingHeaders,
  EarnedPendingHeaders,
  RedeemedPendingHeaders,
  nonSalesHeaders,
  bonusHeaders,
} from "./Export/export";
import access from "config/access";
import PointsCard from "./Components/PointsCard";
import ViewMyStatement from "./ViewMyStatement";

// material-ui
import { withStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Badge from "@material-ui/core/Badge";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormControl from "@material-ui/core/FormControl";
import MenuItem from "@material-ui/core/MenuItem";
import { useTranslation } from "react-i18next";

// material-ui icons
import MoreVertIcon from "@material-ui/icons/MoreVert";

// ant-ui
import { Popover } from "antd";

// Helpers
import {
  pointsFormat,
  currencyFormat,
  dateFormat,
} from "helpers/dataFormat";
import { logEvent } from "helpers/analytics";
import { useTrackWithGTM } from "helpers/gtm";

// custom TextField
const CssTextField = withStyles({
  root: {
    "& label.Mui-focused": {
      color: "##D3D3D3",
    },
    "& .MuiFormLabel-root": {
      color: "#707070",
    },
    "& .MuiInputBase-input": {
      color: "#707070",
    },
    "& .MuiInput-underline:before": {
      borderBottomColor: "#707070!important",
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: "#707070!important",
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#707070",
      },
      "&:hover fieldset": {
        borderColor: "#707070",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#707070",
      },
      "&.MuiOutlinedInput-root": {
        borderRadius: 20,
      },
      "&.MuiOutlinedInput-root": {
        marginTop: 0,
        borderRadius: "0px 10px 10px 0px !important",
        height: 48,
      },
    },
  },
})(TextField);

const headers = {
  earned: EarnedHeaders,
  nonSales: nonSalesHeaders,
  bonus: bonusHeaders,
  redeemed: RedeemedHeaders,
  pending: PendingHeaders,
  pendingEarned: EarnedPendingHeaders,
  pendingRedeemed: RedeemedPendingHeaders,
};

const exportFileName = {
  earned: "Earned",
  nonSales: "NonSalesEarned",
  bonus: "Bonus",
  redeemed: "Redeemed",
  pending: "EarnedAndRedeemedPendingPoints",
  pendingEarned: "PendingEarned",
  pendingRedeemed: "PendingRedeemed",
};

const tabs = [
  {
    tabName: "Points Earned",
    tabLink: "earned",
  },
  {
    tabName: "Points Redeemed",
    tabLink: "redeemed",
  },
];

const earnedRadioTab = {
  earned: {
    tabName: "Sales",
    tabLink: "earned",
    tabValue: 0,
  },
  nonSales: {
    tabName: "Others",
    tabLink: "nonSales",
    tabValue: 1,
  },
};

const redeemedRadioTab = {
  redeemed: {
    tabName: "Approved",
    tabLink: "redeemed",
    tabValue: 2,
  },
  pendingRedeemed: {
    tabName: "Pending",
    tabLink: "pendingRedeemed",
    tabValue: 3,
  },
};

export default function Dashboard() {
  const { state, dispatch } = useContext(Context);
  const {
    getTransaction,
    doFilter,
    Export,
    getCustomerAccounts,
    getOrg,
  } = DashboardAPI();

  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const [states, setstates] = useState({
    selectedTab: "earned",
    activeTab: 0,
    loyalityAccount: {},
    loading: false,
    earnedRadio: "earned",
    redeemedRadio: "redeemed",
    popover: false,
    startDate: new Date(),
    endDate: new Date(),
    date: false,
    dateValue: 0,
    businessName: [],
    searchValue: {},
    dialog: false,
  });

  const { trackWithGTM } = useTrackWithGTM();

  const setStates = useCallback((newState) => {
    setstates((states) => ({ ...states, ...newState }));
  }, []);

  useEffect(() => {
    logEvent({
      event_type: "page_view",
      event_name: "View my Activity",
      user_id: state?.userInfo?.user_id,
      session_id: state?.userInfo?.session_id,
    });
  }, []);

  // ===========================================================================
  // ORDER DATE
  // ===========================================================================

  const [startDate, setStartDate] = useState({
    earned: new Date(),
    redeemed: new Date(),
  });

  const [endDate, setEndDate] = useState({
    earned: new Date(),
    redeemed: new Date(),
  });

  const [date, setDate] = useState({
    earned: false,
    redeemed: false,
  });

  const [dateValue, setDateValue] = useState({
    earned: 0,
    redeemed: 0,
  });

  const [customFilterDate, setCustomFilterDate] = useState({
    earned: false,
    redeemed: false,
  });

  // ===========================================================================
  // POST DATE
  // ===========================================================================

  const [startPostDate, setStartPostDate] = useState({
    earned: new Date(),
    redeemed: new Date(),
  });

  const [endPostDate, setEndPostDate] = useState({
    earned: new Date(),
    redeemed: new Date(),
  });

  const [postDate, setPostDate] = useState({
    earned: false,
    redeemed: false,
  });

  const [postDateValue, setPostDateValue] = useState({
    earned: 0,
    redeemed: 0,
  });

  const [customPostFilterDate, setCustomPostFilterDate] = useState({
    earned: false,
    redeemed: false,
  });

  // ===========================================================================

  const [orgName, setOrgName] = useState({
    earned: [],
    redeemed: [],
  });

  const [expandPanel, setExpandPanel] = useState({
    earned: false,
    redeemed: false,
  });

  const [searchValue, setSearchValue] = useState({
    earned: "",
    redeemed: "",
  });

  const [filterCard, setFilterCard] = useState({
    earned: false,
    redeemed: false,
  });

  const [searchActive, setSearchActive] = useState({
    earned: false,
    redeemed: false,
  });

  const [filterActive, setFilterActive] = useState({
    earned: false,
    nonSales: false,
    redeemed: false,
  });

  const [customerAccounts, setCustomerAccounts] = useState({
    earned: [],
    redeemed: [],
  });

  const [billingAccounts, setBillingAccounts] = useState({
    earned: [],
    redeemed: [],
  });

  const [exportData, setExportData] = useState([]);

  const [offset, setOffset] = useState({
    earned: 1,
    nonSales: 1,
    redeemed: 1,
    pendingRedeemed: 1,
  });

  const [viewMore, setViewMore] = useState({
    earned: false,
    nonSales: false,
    redeemed: false,
    pendingRedeemed: false,
  });

  const [viewMoreEnd, setViewMoreEnd] = useState({
    earned: "",
    nonSales: "",
    redeemed: "",
    pendingRedeemed: "",
  });

  const [noData, setNoData] = useState({
    earned: false,
    nonSales: false,
    redeemed: false,
    pendingRedeemed: false,
  });

  const [currentTab, setCurrentTab] = useState(
    location?.state?.redeemed ? "pendingRedeemed" : "earned"
  );

  const [data, setData] = useState({
    earned: [],
    nonSales: [],
    redeemed: [],
    pendingRedeemed: [],
  });

  const [count, setCount] = useState({
    earned: 0,
    redeemed: 0,
  });

  const [loading, setLoading] = useState({
    earned: false,
    nonSales: false,
    redeemed: false,
    pendingRedeemed: false,
  });

  const [response, setResponse] = useState({
    earned: false,
    nonSales: false,
    redeemed: false,
    pendingRedeemed: false,
  });

  const [type, setType] = useState(1);
  const [disableDropdown, setDisableDropdown] = useState(false);

  useEffect(() => {
    if (location?.state?.redeemed) {
      setStates({
        selectedTab: "redeemed",
        redeemedRadio: "pendingRedeemed",
        activeTab: 3,
      });

      history.replace("/dashboard/dashboard", { redeemed: false });
    }
  }, []);

  useEffect(() => {
  if (location?.state?.redeemed === "pendingRedeemed") {
    setStates({
      selectedTab: "redeemed",
      redeemedRadio: "pendingRedeemed",
      activeTab: 3,
    });

    setCurrentTab("pendingRedeemed");

    history.replace("/dashboard/dashboard");
  }
}, [location?.state?.redeemed]);


  

  

  useEffect(() => {
    handleMarketingAdminFunc();
  }, [state.ALA]);

  const handleMarketingAdminFunc = () => {
    if (access["myDashboard"]["loyalty"][state.userInfo.role]) {
      if (state?.ALA && Object.keys(state?.ALA).length > 0) {
        return;
      }

      return history.push("/dashboard/loyalty");
    }
  };

  // ===========================================================================
  // TABS
  // ===========================================================================

  function setTab(tab) {
    switch (tab) {
      case 0:
        return (
          <Issued
            loyalityAccount={states.loyalityAccount}
            data={data[currentTab]}
            offset={offset}
            setOffset={setOffset}
            currentTab={currentTab}
            viewMore={viewMore}
            setViewMore={setViewMore}
            viewMoreEnd={viewMoreEnd}
            noData={noData}
            loading={loading[currentTab]}
            response={response[currentTab]}
            count={count[currentTab]}
          />
        );

      case 1:
        return (
          <NonSales
            loyalityAccount={states.loyalityAccount}
            data={data[currentTab]}
            offset={offset}
            setOffset={setOffset}
            currentTab={currentTab}
            viewMore={viewMore}
            setViewMore={setViewMore}
            viewMoreEnd={viewMoreEnd}
            noData={noData}
            loading={loading[currentTab]}
            response={response[currentTab]}
            count={count[currentTab]}
          />
        );

      case 2:
        return (
          <Redeemed
            loyalityAccount={states.loyalityAccount}
            data={data[currentTab]}
            offset={offset}
            setOffset={setOffset}
            currentTab={currentTab}
            viewMore={viewMore}
            setViewMore={setViewMore}
            viewMoreEnd={viewMoreEnd}
            noData={noData}
            loading={loading[currentTab]}
            response={response[currentTab]}
            count={count[currentTab]}
          />
        );

      case 3:
        return (
          <Redeemed
            loyalityAccount={states.loyalityAccount}
            data={data[currentTab]}
            offset={offset}
            setOffset={setOffset}
            currentTab={currentTab}
            viewMore={viewMore}
            setViewMore={setViewMore}
            viewMoreEnd={viewMoreEnd}
            noData={noData}
            loading={loading[currentTab]}
            response={response[currentTab]}
            count={count[currentTab]}
          />
        );

      default:
        return null;
    }
  }

  // ===========================================================================
  // RADIO
  // ===========================================================================

  const handleEarnedRadioChange = (e) => {
    setStates({
      earnedRadio: e.target.value,
      activeTab: earnedRadioTab[e.target.value]?.tabValue,
    });

    logEvent({
      event_type: "Button_Click",
      event_name: `${e.target.value} clicked`,
      page_url: window.location.pathname,
      user_id: state?.userInfo?.user_id,
      session_id: state?.userInfo?.session_id,
    });

    trackWithGTM({
      database: state?.userInfo?.loyalty_organization_name,
      event: "click",
      pathname: window.location.pathname,
      userId: state?.userInfo?.user_id,
      role_id: state?.userInfo?.role_id,
      allstate: state,
    });

    setCurrentTab(e.target.value);
  };

  const handleRedeemedRadioChange = (e) => {
    console.log({ e });

    setStates({
      redeemedRadio: e.target.value,
      activeTab: redeemedRadioTab[e.target.value]?.tabValue,
    });

    setCurrentTab(e.target.value);
  };

  // ===========================================================================
  // BREADCRUMB
  // ===========================================================================

  useEffect(() => {
    dispatch({
      type: "SET_PATHNAME",
      payload: ["HOME", "My Activity"],
    });
  }, []);

  // ===========================================================================
  // CUSTOMER ACCOUNT / ORG API
  // ===========================================================================

  useEffect(() => {
    if (
      state.ALA &&
      Object.keys(state.ALA).length > 0 &&
      state.billingAccount &&
      Object.keys(state.billingAccount).length > 0
    ) {
      handleGetCustomerAccounts();
      handleGetOrg();
      handleSetBillingAccounts();
      return;
    }
  }, [state.ALA, state.billingAccount]);

  const handleGetCustomerAccounts = async () => {
    try {
      let values = state.ALA.loyalty_id;
      let res = await getCustomerAccounts(values);

      if (res) {
        let ca = res?.data?.map(
          (c) => c.customer_account_number
        );

        dispatch({
          type: "SET_CUSTOMERACCOUNTS",
          payload: ca,
        });

        setCustomerAccounts({
          ...customerAccounts,
          earned: ca,
          redeemed: ca,
        });
      }
    } catch (e) {
      console.log({ e });
    }
  };

  const handleSetBillingAccounts = async () => {
    try {
      let value = [];

      value = [
        state.billingAccount?.customer_account_number,
      ];

      setBillingAccounts({
        ...billingAccounts,
        earned: value,
        redeemed: value,
      });
    } catch (e) {
      console.log({ e });
    }
  };

  const handleGetOrg = async () => {
    try {
      let res = await getOrg();

      if (res) {
        let org = res.data?.map(
          (c) => c.loyalty_organization_name
        );

        dispatch({
          type: "SET_ORG",
          payload: org,
        });

        setOrgName({
          ...orgName,
          earned: org,
          redeemed: org,
        });
      }
    } catch (e) {
      console.log({ e });
    }
  };

  // ===========================================================================
  // API CALL
  // ===========================================================================

  useEffect(() => {
    if (state.ALA && Object.keys(state.ALA).length > 0) {
      if (
        viewMoreEnd[currentTab] &&
        viewMoreEnd[currentTab].length > 0
      ) {
        return;
      }

      if (filterActive[currentTab]) {
        handleFilter();
        return;
      } else {
        handleGetTransaction();
        return;
      }
    }
  }, [state.ALA, currentTab, offset]);

  // ===========================================================================
  // GET TRANSACTION
  // ===========================================================================

  const handleGetTransaction = async () => {
    setLoading({
      ...loading,
      [currentTab]: true,
    });

    setResponse({
      ...response,
      [currentTab]: false,
    });

    try {
      let values = {
        offset: (offset[currentTab] - 1) * 10,
        limit: 10,
        loyaltyId: state.ALA?.loyalty_id,
      };

      if (currentTab == "earned") {
        values = {
          ...values,
          type: "Earn",
          transaction_status: "Processed",
        };
      }

      if (currentTab == "nonSales") {
        values = {
          ...values,
          type: "Earn",
          transaction_status: "Processed",
          transaction_type: currentTab,
        };
      }

      if (currentTab == "redeemed") {
        values = {
          ...values,
          type: "Burn",
          transaction_status: "Processed",
        };
      }

      if (currentTab == "pendingRedeemed") {
        values = {
          ...values,
          type: "Burn",
          transaction_status: "Inprogress",
        };
      }

      let res = await getTransaction(values);

      if (res) {
        if (viewMore[currentTab]) {
          setData({
            ...data,
            [currentTab]: [
              ...data[currentTab],
              ...res.data,
            ],
          });

          setCount({
            ...count,
            [currentTab]: res.count,
          });

          if (res.data.length < 6) {
            setViewMoreEnd({
              ...viewMoreEnd,
              [currentTab]: "End of data",
            });
          }
        } else {
          setData({
            ...data,
            [currentTab]: res.data,
          });

          setCount({
            ...count,
            [currentTab]: res.count,
          });

          setViewMoreEnd({
            ...viewMoreEnd,
            [currentTab]: "",
          });

          if (res?.count == 0) {
            setNoData({
              ...noData,
              [currentTab]: true,
            });
          }

          if (res?.count > 0) {
            if (
              res.data.length <= 6 &&
              res?.count <= 6
            ) {
              setNoData({
                ...noData,
                [currentTab]: true,
              });

              setViewMoreEnd({
                ...viewMoreEnd,
                [currentTab]: "End of data",
              });
            }
          }
        }

        setResponse({
          ...response,
          [currentTab]: true,
        });

        setLoading({
          ...loading,
          [currentTab]: false,
        });

        return;
      }

      setResponse({
        ...response,
        [currentTab]: true,
      });
    } catch (e) {
      console.log("Dashboard", { e });
    }
  };

  // ===========================================================================
  // FILTER API
  // ===========================================================================

  const handleFilter = async () => {
    setLoading({
      ...loading,
      [currentTab]: true,
    });

    try {
      let values = {};

      var startOrderDate;
      var endOrderDate;

      values = {
        ...values,
        transaction_status: "Processed",
        offset: (offset[currentTab] - 1) * 10,
        limit: 10,
        loyaltyId: state.ALA?.loyalty_id,
      };

      // -----------------------------------------------------------------------
      // Search value
      // -----------------------------------------------------------------------

      if (searchValue[currentTab]) {
        values = {
          ...values,
          searchValue: searchValue[currentTab],
        };
      }

      // -----------------------------------------------------------------------
      // ORDER DATE FILTER
      // -----------------------------------------------------------------------

      if (customFilterDate[currentTab]) {
        values = {
          ...values,
          startOrderDate: startDate[currentTab],
          endOrderDate: endDate[currentTab],
        };
      } else if (date[currentTab]) {
        startOrderDate = moment()
          .subtract(dateValue[currentTab], "d")
          .format("MM/DD/YYYY");

        endOrderDate = moment(new Date())
          .format("MM/DD/YYYY");

        values = {
          ...values,
          startOrderDate: startOrderDate,
          endOrderDate: endOrderDate,
        };
      }

      // -----------------------------------------------------------------------
      // POST DATE FILTER - NEW
      // -----------------------------------------------------------------------

      if (customPostFilterDate[currentTab]) {
        values = {
          ...values,
          startPostDate: startPostDate[currentTab],
          endPostDate: endPostDate[currentTab],
        };
      } else if (postDate[currentTab]) {
        const startPostDateValue = moment()
          .subtract(postDateValue[currentTab], "d")
          .format("MM/DD/YYYY");

        const endPostDateValue = moment(new Date())
          .format("MM/DD/YYYY");

        values = {
          ...values,
          startPostDate: startPostDateValue,
          endPostDate: endPostDateValue,
        };
      }

      // -----------------------------------------------------------------------
      // Org name
      // -----------------------------------------------------------------------

      if (
        orgName[currentTab] &&
        orgName[currentTab].length > 0
      ) {
        values = {
          ...values,
          orgName: orgName[currentTab],
        };
      }

      // -----------------------------------------------------------------------
      // Earned
      // -----------------------------------------------------------------------

      if (currentTab == "earned") {
        values = {
          ...values,
          type: "Earn",
          customerAccounts: customerAccounts[currentTab],
        };
      }

      // -----------------------------------------------------------------------
      // Redeemed
      // -----------------------------------------------------------------------

      if (currentTab == "redeemed") {
        values = {
          ...values,
          type: "Burn",
          billingAccounts: billingAccounts[currentTab],
        };
      }

      // -----------------------------------------------------------------------
      // Non Sales
      // -----------------------------------------------------------------------

      if (currentTab == "nonSales") {
        values = {
          ...values,
          type: "Earn",
          transaction_type: currentTab,
        };
      }

      console.log("FILTER VALUES:", values);

      let res = await doFilter(values);

      if (res) {
        if (count[currentTab] == 0) {
          setCount({
            ...count,
            [currentTab]: res.count,
          });
        }

        if (viewMore[currentTab]) {
          setData({
            ...data,
            [currentTab]: [
              ...data[currentTab],
              ...res.data,
            ],
          });

          if (res.data.length < 6) {
            setViewMoreEnd({
              ...viewMoreEnd,
              [currentTab]: "End of data",
            });
          }
        } else {
          setData({
            ...data,
            [currentTab]: res.data,
          });

          if (res?.count == 0) {
            setNoData({
              ...noData,
              [currentTab]: true,
            });
          }

          if (res?.count > 0) {
            if (
              res.data.length <= 6 &&
              res?.count <= 6
            ) {
              setNoData({
                ...noData,
                [currentTab]: true,
              });

              setViewMoreEnd({
                ...viewMoreEnd,
                [currentTab]: "End of data",
              });
            }
          }
        }

        setFilterCard({
          ...filterCard,
          [currentTab]: false,
        });

        setResponse({
          ...response,
          [currentTab]: true,
        });

        setLoading({
          ...loading,
          [currentTab]: false,
        });
      } else {
        setData({
          ...data,
          [currentTab]: [],
        });

        setFilterCard({
          ...filterCard,
          [currentTab]: false,
        });

        setResponse({
          ...response,
          [currentTab]: true,
        });

        setLoading({
          ...loading,
          [currentTab]: false,
        });
      }
    } catch (e) {
      console.log("Dashboard", { e });
    }
  };

  // ===========================================================================
  // EXPORT API
  // ===========================================================================

  const handleExport = async () => {
    try {
      let values = {};

      var startOrderDate;
      var endOrderDate;

      // -----------------------------------------------------------------------
      // If filter is NOT active
      // -----------------------------------------------------------------------

      if (!filterActive[currentTab]) {
        values = {
          condition: "GetAll",
          loyaltyId: state.ALA?.loyalty_id,
        };

        if (currentTab == "earned") {
          values = {
            ...values,
            type: "Earn",
            transaction_status: "Processed",
          };
        }

        if (currentTab == "nonSales") {
          values = {
            ...values,
            type: "Earn",
            transaction_status: "Processed",
            transaction_type: currentTab,
          };
        }

        if (currentTab == "redeemed") {
          values = {
            ...values,
            type: "Burn",
          };
        }

        if (currentTab == "pendingRedeemed") {
          values = {
            ...values,
            type: "Burn",
            transaction_status: "Inprogress",
          };
        }
      }

      // -----------------------------------------------------------------------
      // If filter IS active
      // -----------------------------------------------------------------------

      if (filterActive[currentTab]) {
        values = {
          ...values,
          condition: "Filter",
          transaction_status: "Processed",
          loyaltyId: state.ALA?.loyalty_id,
        };

        // Earned
        if (currentTab == "earned") {
          values = {
            ...values,
            type: "Earn",
            customerAccounts: customerAccounts[currentTab],
          };
        }

        // Non sales
        if (currentTab == "nonSales") {
          values = {
            ...values,
            type: "Earn",
            transaction_status: "Processed",
            transaction_type: currentTab,
          };
        }

        // Redeemed
        if (currentTab == "redeemed") {
          values = {
            ...values,
            type: "Burn",
          };

          if (
            billingAccounts[currentTab] &&
            billingAccounts[currentTab].length > 0
          ) {
            values = {
              ...values,
              billingAccounts: billingAccounts[currentTab],
            };
          }
        }

        // ---------------------------------------------------------------------
        // Search value
        // ---------------------------------------------------------------------

        if (
          searchValue[currentTab] &&
          searchValue[currentTab].length != 0
        ) {
          values = {
            ...values,
            searchValue: searchValue[currentTab],
          };
        }

        // ---------------------------------------------------------------------
        // ORDER DATE FILTER
        // ---------------------------------------------------------------------

        if (customFilterDate[currentTab]) {
          values = {
            ...values,
            startOrderDate: startDate[currentTab],
            endOrderDate: endDate[currentTab],
          };
        } else if (date[currentTab]) {
          startOrderDate = moment()
            .subtract(dateValue[currentTab], "d")
            .format("MM/DD/YYYY");

          endOrderDate = moment(new Date())
            .format("MM/DD/YYYY");

          values = {
            ...values,
            startOrderDate,
            endOrderDate,
          };
        }

        // ---------------------------------------------------------------------
        // POST DATE FILTER - NEW
        // ---------------------------------------------------------------------

        if (customPostFilterDate[currentTab]) {
          values = {
            ...values,
            startPostDate: startPostDate[currentTab],
            endPostDate: endPostDate[currentTab],
          };
        } else if (postDate[currentTab]) {
          const startPostDateValue = moment()
            .subtract(postDateValue[currentTab], "d")
            .format("MM/DD/YYYY");

          const endPostDateValue = moment(new Date())
            .format("MM/DD/YYYY");

          values = {
            ...values,
            startPostDate: startPostDateValue,
            endPostDate: endPostDateValue,
          };
        }

        // ---------------------------------------------------------------------
        // Org name
        // ---------------------------------------------------------------------

        if (
          orgName[currentTab] &&
          orgName[currentTab].length > 0
        ) {
          values = {
            ...values,
            orgName: orgName[currentTab],
          };
        }
      }

      console.log("EXPORT VALUES:", values);

      let res = await Export(values);

      if (res.data) {
        const convertedArray = res.data.map((obj) => {
          return Object.keys(obj).reduce((acc, key) => {
            if (
              key == "points" ||
              key == "points_redeemed" ||
              key == "credits_applied"
            ) {
              if (i18n.language == "fr") {
                acc[key] =
                  pointsFormat(
                    obj[key],
                    i18n.language
                  ) +
                  String.fromCharCode(8203);
              } else {
                acc[key] = pointsFormat(
                  obj[key],
                  i18n.language
                );
              }
            } else if (
              key == "extended_sales_amount" ||
              key == "credit_issued" ||
              key == "purchase_value"
            ) {
              acc[key] =
                state?.userInfo
                  ?.loyalty_organization_name ==
                  "Henry Schein Dental" ||
                key == "purchase_value" ||
                key == "extended_sales_amount"
                  ? currencyFormat(
                      obj[key],
                      i18n.language,
                      state?.userInfo
                        ?.loyalty_organization_name
                    )
                  : key == "credit_issued" &&
                    new Date(
                      obj["date_of_transaction"]
                    ) <
                      new Date("2023-10-31")
                  ? "PRIV"
                  : currencyFormat(
                      obj[key],
                      i18n.language,
                      state?.userInfo
                        ?.loyalty_organization_name
                    );
            } else if (
              key == "order_date" ||
              key == "post_date" ||
              key == "reedemption_date" ||
              key == "date_of_transaction" ||
              key == "points_expiration_date"
            ) {
              if (i18n.language == "fr") {
                acc[key] = obj[key]
                  ? state?.userInfo
                      ?.loyalty_organization_name ==
                      "Henry Schein Canada" &&
                    key ==
                      "points_expiration_date" &&
                    obj[key] == "N/A"
                    ? "N/A"
                    : dateFormat(
                        obj[key],
                        i18n.language
                      ) +
                      String.fromCharCode(8203)
                  : "";
              } else {
                acc[key] = obj[key]
                  ? state?.userInfo
                      ?.loyalty_organization_name ==
                      "Henry Schein Canada" &&
                    key ==
                      "points_expiration_date" &&
                    obj[key] == "N/A"
                    ? "N/A"
                    : dateFormat(
                        obj[key],
                        i18n.language
                      )
                  : "";
              }

              if (
                key == "points_expiration_date" &&
                state?.ALA?.[
                  "Loyalty_Account.points_not_expiring"
                ] == true
              ) {
                acc[key] = "N/A";
              }
            } else {
              acc[key] = t(obj[key]);
            }

            return acc;
          }, {});
        });

        setExportData(convertedArray);
        return true;
      }

      return false;
    } catch (e) {
      console.log("Dashboard", { e });
    }
  };

  // ===========================================================================
  // HSD
  // ===========================================================================

  const isHSD =
    state?.userInfo?.loyalty_organization_name ===
      "Henry Schein Dental" &&
    state?.userInfo?.loyalty_site_id ==
      "henryschein.com";

  // ===========================================================================
  // RETURN
  // ===========================================================================

  return (
    <React.Fragment>
      {type == 1 && (
        <div>
          <PointsCard
            loyaltyId={state?.ALA?.loyalty_id} showThriveUnlocked={false}  showbenefitmetric={false}
          />

          

          <AvailablePointsCard
            loyaltyActive={
              state?.ALA?.[
                "Loyalty_Account.is_active"
              ]
            }
          />

          <Grid
            container
            spacing={0}
            className={
              access["dashboard"]["changeLoyalty"][
                state.userInfo.role
              ]
                ? classes.alignGridRightOne
                : classes.alignGridRightTwo
            }
            style={{
              marginTop: isHSD
                ? "10px"
                : "-56px",
            }}
          >
            {tabs.map((e, i) => (
              <Grid
                key={i}
                item
                style={{ flex: "none" }}
                className="pending-form-gp"
              >
                <React.Fragment key={t(e.tabName)}>
                  <button
                    type="button"
                    className={`${
                      states.selectedTab ==
                      e.tabLink
                        ? classes.dash_1_tab_1
                        : classes.dash_1_tab_2
                    } ${
                      states.selectedTab ==
                        e.tabLink &&
                      classes.earnedBtn
                    } ${
                      e.tabLink ===
                        "redeemed" &&
                      classes.reedemBtn
                    }`}
                    key={i}
                    onClick={() => {
                      logEvent({
                        event_type:
                          "Button_Click",
                        event_name: `${e.tabLink} tab clicked`,
                        page_url:
                          window.location.pathname,
                        user_id:
                          state?.userInfo
                            ?.user_id,
                        session_id:
                          state?.userInfo
                            ?.session_id,
                      });

                      trackWithGTM({
                        database:
                          state?.userInfo
                            ?.loyalty_organization_name,
                        event: "click",
                        pathname:
                          window.location.pathname,
                        userId:
                          state?.userInfo?.user_id,
                        role_id:
                          state?.userInfo?.role_id,
                        allstate: state,
                      });

                      let obj = {};

                      if (
                        e.tabLink ===
                        "earned"
                      ) {
                        obj = {
                          selectedTab:
                            e.tabLink,
                          activeTab: i,
                          popover: false,
                          earnedRadio:
                            e.tabLink,
                        };
                      } else if (
                        e.tabLink ===
                        "redeemed"
                      ) {
                        obj = {
                          selectedTab:
                            e.tabLink,
                          activeTab: 2,
                          popover: false,
                          redeemedRadio:
                            e.tabLink,
                        };
                      }

                      setStates(obj);
                      setCurrentTab(
                        e.tabLink
                      );
                    }}
                  >
                    {t(e.tabName).toUpperCase()}
                  </button>
                </React.Fragment>

                {(currentTab ===
                  "earned" ||
                  currentTab ===
                    "nonSales") &&
                  i == 0 && (
                    <span>
                      <CssTextField
                        variant="outlined"
                        id={"dropdown"}
                        select
                        type={"text"}
                        className={
                          classes.earnedInput
                        }
                        required
                        value={
                          states.earnedRadio
                        }
                        onChange={
                          handleEarnedRadioChange
                        }
                      >
                        {Object.keys(
                          earnedRadioTab
                        ).map(
                          (key, i) => (
                            <MenuItem
                              value={
                                earnedRadioTab[
                                  key
                                ][
                                  "tabLink"
                                ]
                              }
                              key={t(
                                earnedRadioTab[
                                  key
                                ][
                                  "tabName"
                                ]
                              )}
                            >
                              {t(
                                earnedRadioTab[
                                  key
                                ][
                                  "tabName"
                                ]
                              )}
                            </MenuItem>
                          )
                        )}
                      </CssTextField>
                    </span>
                  )}
              </Grid>
            ))}

            {(!(
              state?.ALA?.[
                "Loyalty_Account.dormant_paused_date"
              ]) ||
              state?.userInfo?.role_id !=
                null) &&
              (currentTab ==
                "earned" ||
                currentTab ==
                  "nonSales" ||
                currentTab ==
                  "redeemed") && (
                <React.Fragment>
                  <Grid
                    item
                    className={
                      classes.filterGrid
                    }
                  >
                    <Button
                      className={`${classes.filterBtn} dash-1-filter-btn`}
                      onClick={() => {
                        setFilterCard({
                          ...filterCard,
                          [currentTab]:
                            !filterCard[
                              currentTab
                            ],
                        });

                        setStates({
                          popover: false,
                        });

                        if (
                          !filterActive[
                            currentTab
                          ]
                        ) {
                          setCustomerAccounts({
                            ...customerAccounts,
                            earned:
                              state.customerAccounts,
                            redeemed:
                              state.customerAccounts,
                          });

                          // Reset Order Date
                          setDate({
                            ...date,
                            [currentTab]:
                              false,
                          });

                          setDateValue({
                            ...dateValue,
                            [currentTab]:
                              0,
                          });

                          setCustomFilterDate({
                            ...customFilterDate,
                            [currentTab]:
                              false,
                          });

                          setStartDate({
                            ...startDate,
                            [currentTab]:
                              new Date(),
                          });

                          setEndDate({
                            ...endDate,
                            [currentTab]:
                              new Date(),
                          });

                          // Reset Post Date
                          setPostDate({
                            ...postDate,
                            [currentTab]:
                              false,
                          });

                          setPostDateValue({
                            ...postDateValue,
                            [currentTab]:
                              0,
                          });

                          setCustomPostFilterDate({
                            ...customPostFilterDate,
                            [currentTab]:
                              false,
                          });

                          setStartPostDate({
                            ...startPostDate,
                            [currentTab]:
                              new Date(),
                          });

                          setEndPostDate({
                            ...endPostDate,
                            [currentTab]:
                              new Date(),
                          });
                        }
                      }}
                    >
                      {t("FILTER")}&nbsp;

                      {filterActive[
                        currentTab
                      ] &&
                      response[
                        currentTab
                      ] ? (
                        <Badge
                          badgeContent={
                            count[
                              currentTab
                            ] == 0
                              ? ""
                              : count[
                                  currentTab
                                ]
                          }
                          classes={{
                            badge:
                              classes.badge,
                          }}
                          max={999}
                        ></Badge>
                      ) : null}
                    </Button>
                  </Grid>
                </React.Fragment>
              )}

            {(!(
              state?.ALA?.[
                "Loyalty_Account.dormant_paused_date"
              ]) ||
              state?.userInfo?.role_id !=
                null) && (
              <Grid
                item
                className={
                  classes.tripleDot
                }
              >
                <span className="vert-icon-wrapper">
                  <Popover
                    placement={"bottom"}
                    color="#4472c4"
                    content={
                      <React.Fragment>
                        <div
                          className={
                            classes.exportParentDiv
                          }
                        >
                          <div
                            className={
                              classes.exportDiv
                            }
                            onClick={async (
                              event,
                              done
                            ) => {
                              logEvent({
                                event_type:
                                  "Button_Click",
                                event_name:
                                  "Export_view_My_Activity",
                                page_url:
                                  window.location
                                    .pathname,
                                user_id:
                                  state?.userInfo
                                    ?.user_id,
                                session_id:
                                  state?.userInfo
                                    ?.session_id,
                              });

                              trackWithGTM({
                                database:
                                  state?.userInfo
                                    ?.loyalty_organization_name,
                                event: "click",
                                pathname:
                                  window.location
                                    .pathname,
                                userId:
                                  state?.userInfo
                                    ?.user_id,
                                role_id:
                                  state?.userInfo
                                    ?.role_id,
                                allstate:
                                  state,
                              });

                              let res =
                                await handleExport();

                              if (res)
                                document
                                  .getElementById(
                                    "csv"
                                  )
                                  .click();
                            }}
                          >
                            {t("Export")}
                          </div>
                        </div>
                      </React.Fragment>
                    }
                    trigger="click"
                    visible={
                      states.popover
                    }
                  >
                    <MoreVertIcon
                      onClick={() => {
                        setStates({
                          popover:
                            !states.popover,
                        });

                        setFilterCard({
                          ...filterCard,
                          [currentTab]:
                            false,
                        });
                      }}
                      className={
                        classes.popover
                      }
                      style={{
                        margin: 0,
                      }}
                    />
                  </Popover>
                </span>
              </Grid>
            )}
          </Grid>

          {/* ================================================================
              FILTER COMPONENT
              ================================================================ */}

          {filterCard[currentTab] && (
            <Filter
              states={states}
              setStates={setStates}
              setCustomFilterDate={
                setCustomFilterDate
              }

              offset={offset}
              setOffset={setOffset}
              currentTab={currentTab}
              filterCard={filterCard}
              setFilterCard={setFilterCard}
              handleGetTransaction={
                handleGetTransaction
              }
              handleFilter={handleFilter}

              // --------------------------------------------------------------
              // Existing Order Date
              // --------------------------------------------------------------

              customFilterDate={
                customFilterDate
              }

              startDate={startDate}
              setStartDate={setStartDate}

              endDate={endDate}
              setEndDate={setEndDate}

              date={date}
              setDate={setDate}

              dateValue={dateValue}
              setDateValue={setDateValue}

              // --------------------------------------------------------------
              // NEW Post Date
              // --------------------------------------------------------------

              startPostDate={
                startPostDate
              }

              setStartPostDate={
                setStartPostDate
              }

              endPostDate={
                endPostDate
              }

              setEndPostDate={
                setEndPostDate
              }

              postDate={postDate}
              setPostDate={setPostDate}

              postDateValue={
                postDateValue
              }

              setPostDateValue={
                setPostDateValue
              }

              customPostFilterDate={
                customPostFilterDate
              }

              setCustomPostFilterDate={
                setCustomPostFilterDate
              }

              // --------------------------------------------------------------

              customerAccounts={
                customerAccounts
              }
              setCustomerAccounts={
                setCustomerAccounts
              }

              billingAccounts={
                billingAccounts
              }
              setBillingAccounts={
                setBillingAccounts
              }

              filterActive={
                filterActive
              }
              setFilterActive={
                setFilterActive
              }

              orgName={orgName}
              setOrgName={setOrgName}

              expandPanel={
                expandPanel
              }
              setExpandPanel={
                setExpandPanel
              }

              setViewMore={
                setViewMore
              }

              setViewMoreEnd={
                setViewMoreEnd
              }

              setNoData={
                setNoData
              }

              viewMore={viewMore}
              viewMoreEnd={
                viewMoreEnd
              }

              noData={noData}

              data={data}
              setData={setData}

              response={response}
              setResponse={setResponse}

              count={count}
              setCount={setCount}

              searchValue={
                searchValue
              }
              setSearchValue={
                setSearchValue
              }
            />
          )}

          {/* ================================================================
              CSV EXPORT
              ================================================================ */}

          <CSVLink
            id="csv"
            data={exportData}
            separator={","}
            headers={headers?.[
              currentTab
            ]
              ?.map((head) => {
                if (
                  state?.userInfo
                    ?.loyalty_organization_name ===
                    "Henry Schein Dental" &&
                  currentTab ===
                    "earned"
                ) {
                  if (
                    head.label ===
                    "Order ID"
                  ) {
                    return {
                      ...head,
                      label: t(
                        head.label
                      ),
                    };
                  }
                }

                if (
                  head.label !==
                  "Order ID"
                ) {
                  return {
                    ...head,
                    label: t(
                      head.label
                    ),
                  };
                }

                return undefined;
              })
              ?.filter(Boolean)}
            filename={
              t(
                exportFileName[
                  currentTab
                ]
              ) +
              new Date().toLocaleString() +
              ".csv"
            }
            style={{
              display: "none",
            }}
            onClick={() => {
              setStates({
                popover: false,
              });
            }}
          />

          <div
            className={
              classes.tabsContainer
            }
          >
            {setTab(
              states.activeTab
            )}
          </div>
        </div>
      )}
    </React.Fragment>
  );
}

